import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ProjectType } from '@project/model/project-type';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { lastValueFrom } from 'rxjs';
import { WizardModel } from '../wizard-model';
import { ArticleDataService } from '@article/service/article-data.service';
import { OverlayService } from '@shared/overlay/overlay.service';
import { RouteNavigationService } from '@services/route-navigation.service';
import { ConfigurationCreationService } from '../../service/configuration-creation.service';
import { McsGdsProjectCreationProcessor } from '../../service/mcs-gds-project-creation.processor';
import { ArticleWithTechnicalPropertiesDTO } from '@article/properties/technical/technical-requirements';
import { MiniTerminalBlocksArticleManager } from '@article/manager/mini-terminal-blocks-article.manager';
import { BaseArticleProjectProcessorFactory } from '@article/model/base-article-project-processor-factory';
import { ArticleContainerAttachedService } from '@project/components/article/service/article-container-attached.service';
import { ProjectModel } from '@project/model/project.model';
import { ProjectModelService } from '@project/service/project-model.service';
import { AlertHelper } from '@shared/alert/alert.helper';
import { MiniTerminalBlocksProjectCreationService } from '../../service/mini-terminal-blocks-project-creation.service';
import { Injectable } from '@angular/core';
import { Log } from '@shared/debug/log';

@Injectable()
export abstract class MCSGDSModel extends WizardModel {
  public abstract readonly type: 'MCS' | 'GDS';

  private _article?: ArticleWithTechnicalPropertiesDTO;

  constructor(
    private alertHelper: AlertHelper,
    private miniTerminalBlocksArticleManager: MiniTerminalBlocksArticleManager,
    private baseArticleProjectProcessorFactory: BaseArticleProjectProcessorFactory,
    private projectModel: ProjectModel,
    private projectModelService: ProjectModelService,
    private articleContainerAttachedService: ArticleContainerAttachedService,
    private miniTerminalBlocksProjectCreationService: MiniTerminalBlocksProjectCreationService,
    private configurationCreationService: ConfigurationCreationService,
    private articleDataService: ArticleDataService,
    private overlayService: OverlayService,
    private routeNavigationService: RouteNavigationService,
  ) {
    super();
  }

  public selectValue(step: number, value: any): void {
    const currentStepConfig = this.config.steps[step];
    if (step < 0 || !currentStepConfig) {
      return;
    }
    this._article = value;
  }

  public async create(): Promise<void> {
    if (this.creationStarted) {
      return;
    }
    this.creationStarted = true;

    const processor: McsGdsProjectCreationProcessor = this.getMcsGdsProjectCreationProcessor();
    try {
      await this.createNewProject(processor);
    } catch (error) {
      Log.error('Could not create configuraion: ', error);
    } finally {
      processor?.clear();
      this.creationStarted = false;
    }
  }

  private getMcsGdsProjectCreationProcessor(): McsGdsProjectCreationProcessor {
    return new McsGdsProjectCreationProcessor(
      this.projectModel,
      this.projectModelService,
      this.alertHelper,
      this.miniTerminalBlocksArticleManager,
      this.baseArticleProjectProcessorFactory,
      this.articleContainerAttachedService,
      this.miniTerminalBlocksProjectCreationService
    );
  }

  private async createNewProject(processor: McsGdsProjectCreationProcessor): Promise<void> {
    const article = this._article;
    if (!article) {
      return;
    }

    await this.overlayService.doWithOverlay(async () => {
      await this.configurationCreationService.prepareNewConfiguration(this.metaData);

      let projectType: ProjectType | undefined;
      if (ArticleUtils.isMcsProjectArticleType(article.articleType)) {
        projectType = "MCS";
      } else if (ArticleUtils.isGdsProjectArticleType(article.articleType)) {
        projectType = "GDS";
      }

      if (!projectType) {
        return;
      }


      const articles: ArticleVO[] = await lastValueFrom(this.articleDataService.getLatestArticles([article.articleId]));
      await this.routeNavigationService.toConfigurator();

      await processor?.assembleProject(articles[0], projectType);
    }, OverlayBackground.color);
  }
}
